import React, { useState, useEffect } from "react";
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './Submenu';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AuthService } from 'services/AuthService';
import { EmployeeService } from 'services/EmployeeService';
import Chip from '@mui/material/Chip';
import { parseUserManagementProfilesRoute } from 'utils/userManagementProfilesRouting';

interface PermissionItem {
    id: number;
    permission_id: number;
    name: string;
}

interface ViewItem {
    id: number;
    view_id: number;
    name: string;
}

interface ProfileData {
    profile_id: number;
    name: string;
    permissions: any[];
    views: any[];
    tags: any[];
    update_dtm: string;
}

const ProfileTable = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [rows, setRows] = useState<{
        id: number;
        name: string;
        profile_id: number;
        permissions_amount: number;
        permissions: any;
        views: any;
        tags: any;
        update_dtm: string;
        views_amount: number;
        tags_amount: number;
    }[]>([]);
    const [permissions, setPermissions] = useState<PermissionItem[]>([]);
    const [views, setViews] = useState<ViewItem[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState<GridInputRowSelectionModel | undefined>([]);
    const [selected, setSelected] = useState<any>([]);

    const routeAction = parseUserManagementProfilesRoute(location.pathname);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('profiles.table.columns.name'), flex: 1, type: 'string', filterable: true },
        {
            field: 'permissions_amount',
            headerName: t('profiles.table.columns.permissions'),
            flex: 1,
            align: 'center',
            type: 'number',
            filterable: false,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Chip
                        key={params.row.id}
                        label={params.row.permissions_amount}
                        variant="outlined"
                        size="small"
                        style={{ margin: '2px' }}
                    />
                );
            }
        },
        { field: 'update_dtm', headerName: t('profiles.table.columns.update_dtm'), flex: 1, align: 'center', type: 'string', filterable: false, headerAlign: 'center' },
        {
            field: 'views_amount',
            headerName: t('profiles.table.columns.views'),
            flex: 1,
            align: 'center',
            type: 'number',
            filterable: false,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Chip
                        key={params.row.id}
                        label={params.row.views_amount}
                        variant="outlined"
                        size="small"
                        style={{ margin: '2px' }}
                    />
                );
            }
        },
        {
            field: 'tags_amount',
            headerName: t('profiles.table.columns.tags'),
            flex: 1, align: 'center',
            type: 'number',
            filterable: false,
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Chip
                        key={params.row.id}
                        label={params.row.tags_amount}
                        variant="outlined"
                        size="small"
                        style={{ margin: '2px' }}
                    />
                );
            }
        },
    ];

    const reloading = async () => {
        setLoading(true);

        const authService = new AuthService();
        const employeeService = new EmployeeService();
        const [
            permissionsData,
            profilesData,
            viewsData,
            tagsData
        ] = await Promise.allSettled([
            authService.permission.getPermissions(),
            authService.profile.getProfiles(),
            authService.view.getViews(),
            employeeService.employee.getTags()
        ]) as [
                PromiseSettledResult<PermissionItem[]>,
                PromiseSettledResult<ProfileData[]>,
                PromiseSettledResult<ViewItem[]>,
                PromiseSettledResult<any[]>
            ];

        if (viewsData.status === 'fulfilled') {
            setViews(viewsData.value);
        } else {
            setViews([]);
        }
        if (permissionsData.status === 'fulfilled') {
            setPermissions(permissionsData.value);
        } else {
            setPermissions([]);
        }
        if (tagsData.status === 'fulfilled') {
            setTags(tagsData.value);
        } else {
            setTags([]);
        }
        if (profilesData.status === 'fulfilled') {
            const records = profilesData.value?.map((data: ProfileData) => ({
                id: data.profile_id,
                name: data.name,
                profile_id: data.profile_id,
                permissions_amount: data.permissions.length || 0,
                permissions: data.permissions,
                views: data.views,
                tags: data.tags,
                update_dtm: new Date(data.update_dtm + 'Z').toLocaleString(),
                views_amount: data.views.length || 0,
                tags_amount: data.tags.length || 0,
            }));
            setRows(records);
        } else {
            setRows([]);
        }
        setLoading(false);
    }


    useEffect(() => {
        reloading();
    }, []);

    useEffect(() => {
        if (!routeAction.id || rows.length === 0) {
            return;
        }

        const match = rows.find((row) => Number(row.id) === Number(routeAction.id));
        if (!match) {
            return;
        }

        setSelectedProfile([match.id]);
        setSelected([match]);
    }, [routeAction.id, rows]);

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                loading={loading}
                checkboxSelection
                pagination
                localeText={
                    {
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                                : `${count.toLocaleString()} fila seleccionada`,

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }
                }

                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let select = rows.filter((row) => newRowSelectionModel.includes(row.id))
                    setSelectedProfile(newRowSelectionModel);
                    setSelected(select);
                }}
                rowSelectionModel={selectedProfile}
            >

            </DataGridPro>
            <SubMenu
                selected={selected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
                permissions={permissions}
                views={views}
                tags={tags}
                routeAction={routeAction.action}
            />

        </div>

    )

}

export { ProfileTable };